import ChipSelector from 'components/chip-selector';
import { ProteinTagValues } from 'constants/segmentation';
import { FilterConfig, possibleTags } from 'interfaces/collection-filter';
import { memo, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

interface Props
  extends Omit<
    React.DetailedHTMLProps<
      React.LabelHTMLAttributes<HTMLDivElement>,
      HTMLDivElement
    >,
    'onSelect'
  > {
  availableFilterMap?: FilterConfig;
  selectedOptions?: Array<ProteinTagValues | possibleTags>;
  onSelectTastePreference: (value: ProteinTagValues | possibleTags) => void;
}

/**
 * Renders selectable taste preference chips for pet profile setup.
 * Chips could be disabled when the component is used on a CDP.
 */
const TastePreferenceSelector: React.FC<Props> = ({
  availableFilterMap,
  className,
  selectedOptions,
  onSelectTastePreference,
  ...divProps
}) => {
  const tastePreferences = useMemo(() => {
    const tastePreferencesTags = availableFilterMap?.sorts?.tags;
    return tastePreferencesTags
      ? Object.fromEntries(tastePreferencesTags?.map((tag) => [tag, tag]))
      : ProteinTagValues;
  }, [availableFilterMap]);

  return (
    <div className={className} {...divProps}>
      <span className="text-text-light-bg-primary block font-medium">
        <FormattedMessage id="pet-profile:taste-selector:title" />
      </span>
      <p className="text-text-light-bg-secondary mb-2 text-sm font-normal">
        <FormattedMessage id="pet-profile:taste-selector:description" />
      </p>
      <div className="mt-2 flex flex-wrap gap-3">
        {Object.entries(tastePreferences).map(([, value]) => (
          <ChipSelector
            key={value}
            content={
              <>
                <span data-test={`taste-label-${value}`}>
                  <FormattedMessage id={`cdp:filter:sorts:${value}`} />
                </span>
              </>
            }
            isSelected={selectedOptions?.includes(value) ?? false}
            disabled={false} // Sorts filters are always enabled (PT-8178)
            onClick={() => onSelectTastePreference(value)}
            dataTest={`chip-selector-taste-${value}`}
          />
        ))}
      </div>
    </div>
  );
};

export default memo(TastePreferenceSelector);
